import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';

import TopSection from '../TopSection'
import { getInventoryImpactSimulationData } from '../../../redux/actions/InventoryImpactSimulation';
import { Loader } from '../../../components/common/Loader';
import DataDetails from './DataDetails';
import TextInput from '../../../components/common/TextInput';
import ButtonComponent from '../../../components/common/button/Button';

const InventoryImpactSimulation = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [tableRows, setTableRows] = useState([]);
  const [rowsData, setRowsData] = useState([]);
  const [MfgLTSim, setMfgLTSim] = useState("");
  const [SupplierTTSim, setSupplierTTSim] = useState("");
  const [IQDSIM, setIQDSIM] = useState("");
  const [DWSim, setDWSim] = useState("");
  const [GRPSim, setGRPSim] = useState("");
  const [TMTTSim, setTMTTSim] = useState("");
  const [resetTrigger, setResetTrigger] = useState(0);
  const simulateData = useSelector((state) => state?.getIISimulationDataSlice);

  const pagination = true;
  const paginationPageSize = 30;
  const paginationPageSizeSelector = [30, 50, 100, 500, 2000];

  const parseSimNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getRowId = (row) => row?.sequenceId ?? row?.id;

  const simInputFieldMap = {
    MfgLTSim: { field: "manufacturingLtSim", baseField: "manufacturingLt" },
    SupplierTTSim: { field: "supplierTransitTimeSim", baseField: "supplierTransitTime" },
    IQDSIM: { field: "iqdSim", baseField: "iqd" },
    DWSim: { field: "dockwaitingSim", baseField: "dockWaiting" },
    GRPSim: { field: "grProcessingSim", baseField: "grProcessing" },
    TMTTSim: { field: "tmTransitTimeSim", baseField: "tmTransitTime" },
  };

  const bulkUpdateSelectedRows = (fieldName, previousValue, newValue) => {
    const config = simInputFieldMap[fieldName];
    if (!config || rowsData.length === 0) return;

    const previousNumber = parseSimNumber(previousValue);
    const newNumber = parseSimNumber(newValue);
    const delta = newNumber - previousNumber;
    if (delta === 0) return;

    const selectedRowIds = new Set(rowsData.map(getRowId).filter(Boolean));
    if (selectedRowIds.size === 0) return;

    setTableRows((prevRows) =>
      prevRows.map((row) => {
        if (!selectedRowIds.has(getRowId(row))) {
          return row;
        }

        const currentValue = parseSimNumber(row[config.field] ?? row[config.baseField]);
        return {
          ...row,
          [config.field]: currentValue + delta,
        };
      }),
    );

    setRowsData((prevSelectedRows) =>
      prevSelectedRows.map((row) => {
        const currentValue = parseSimNumber(row[config.field] ?? row[config.baseField]);
        return {
          ...row,
          [config.field]: currentValue + delta,
        };
      }),
    );
  };

  const handleSimulationInputChange = (name, previousValue, setValue) => (e) => {
    const newValue = e?.target?.value ?? "";
    setValue(newValue);
    bulkUpdateSelectedRows(name, previousValue, newValue);
  };

  useEffect(() => {
    if (Array.isArray(simulateData?.data)) {
      setTableRows(simulateData.data);
    }
  }, [simulateData?.data]);

  const handleCellValueChange = async (params) => {
    if (!params?.data) return;

    const updatedRowId = getRowId(params.data);
    if (!updatedRowId) return;

    setTableRows((prevRows) =>
      prevRows.map((row) =>
        getRowId(row) === updatedRowId ? params.data : row,
      ),
    );

    if (rowsData.some((row) => getRowId(row) === updatedRowId)) {
      const updatedSelectedRows = rowsData.map((row) =>
        getRowId(row) === updatedRowId ? params.data : row,
      );
      setRowsData(updatedSelectedRows);
    }
  };

  const onSelectionChanged = (params) => {
    const selectedRowsOnCurrentPage = [];
    const currentPage = params.api.paginationGetCurrentPage();
    const pageSize = params.api.paginationGetPageSize();
    let index = 0;

    params.api.forEachNodeAfterFilterAndSort((node) => {
      const nodePage = Math.floor(index / pageSize);
      if (nodePage === currentPage && node.isSelected()) {
        selectedRowsOnCurrentPage.push(node.data);
      }
      index++;
    });

    setRowsData(selectedRowsOnCurrentPage);
  };

  const rowSelection = useMemo(() => {
    return {
      mode: "multiRow",
      isRowSelectable: (rowNode) =>
        rowNode.data
          ? true
          : false,
    };
  }, []);

  const handleReset = () => { 
    setMfgLTSim("");
    setSupplierTTSim("");
    setIQDSIM("");
    setDWSim("");
    setGRPSim("");
    setTMTTSim("");
    if (Array.isArray(simulateData?.data)) {
      setTableRows(simulateData.data);
    }
    setResetTrigger((prev) => prev + 1);
  }

  const getSimulationData = useCallback(async () => {
      setLoading(true);
      try {
        const response = await dispatch(getInventoryImpactSimulationData());
          if (response?.meta?.requestStatus === 'fulfilled') {
            setLoading(false);
          } else {
            setLoading(false);
            console.error('Failed to fetch data:', response.error);
          }
      } catch(error){
        setLoading(false);
        console.error("Error fetching Price Memo Maintenance data:", error);
      }
    }, [dispatch]);

  useEffect(() => {
    getSimulationData();
  }, [getSimulationData])

  return (
    <>
        <TopSection
            labelList={[
                { title: "Home", path: "/landing-page" },
                { title: "Inventory Impact Simulation" }, 
            ]}
            hideHeader={true}
            headerName={"Inventory Impact Simulation"}
        />
        <div>
          { loading ? 
          <Loader />
          :
          <>
            <div style={{padding: "20px 10px", display: "flex", gap: "20px", alignItems: "center", justifyContent: "space-between", border: "1px solid #ccc", borderRadius: "5px", marginBottom: "10px", flexWrap: "wrap"}}>
              <TextInput
                name="MfgLTSim"
                type="number"
                value={MfgLTSim}
                onChange={handleSimulationInputChange("MfgLTSim", MfgLTSim, setMfgLTSim)}
                label="Mfg Lead time (Sim)"
                width="200px"
                disabled={rowsData?.length === 0}
              />
              <TextInput
                name="SupplierTTSim"
                type="number"
                value={SupplierTTSim}
                onChange={handleSimulationInputChange("SupplierTTSim", SupplierTTSim, setSupplierTTSim)}
                label="Supplier Transit time (Sim)"
                width="200px"
                disabled={rowsData?.length === 0}
              />
              <TextInput
                name="IQDSIM"
                type="number"
                value={IQDSIM}
                onChange={handleSimulationInputChange("IQDSIM", IQDSIM, setIQDSIM)}
                label="IQD (Sim)"
                width="200px"
                disabled={rowsData?.length === 0}
              />
              <TextInput
                name="DWSim"
                type="number"
                value={DWSim}
                onChange={handleSimulationInputChange("DWSim", DWSim, setDWSim)}
                label="Dockwaiting (Sim)"
                width="200px"
                disabled={rowsData?.length === 0}
              />
              <TextInput
                name="GRPSim"
                type="number"
                value={GRPSim}
                onChange={handleSimulationInputChange("GRPSim", GRPSim, setGRPSim)}
                label="GR processing (Sim)"
                width="200px"
                disabled={rowsData?.length === 0}
              />
              <TextInput
                name="TMTTSim"
                type="number"
                value={TMTTSim}
                onChange={handleSimulationInputChange("TMTTSim", TMTTSim, setTMTTSim)}
                label="TM transit time (Sim)"
                width="200px"
                disabled={rowsData?.length === 0}
              />
              <ButtonComponent
                  maxWidth="200px"
                  label="Reset"
                  type="secondary"
                  onClick={handleReset}
                  disabled={MfgLTSim?.length === 0 && GRPSim?.length === 0 && DWSim?.length === 0 && IQDSIM?.length === 0 && SupplierTTSim?.length === 0 && TMTTSim?.length === 0}
              />
            </div>
            <DataDetails 
                row={tableRows}
                pagination={pagination}
                paginationPageSize={paginationPageSize}
                paginationPageSizeSelector={paginationPageSizeSelector}
                onSelectionChanged={onSelectionChanged}
                rowSelection={rowSelection}
                handleCellValueChange={handleCellValueChange}
                MfgLTSim={MfgLTSim}
                SupplierTTSim={SupplierTTSim}
                IQDSIM={IQDSIM}
                DWSim={DWSim}
                GRPSim={GRPSim}
                rowsData={rowsData}
                resetTrigger={resetTrigger}
            />
          </>
          }
        </div>
    </>
  )
}

export default InventoryImpactSimulation